import {
  ContentFields,
  ContentTypeFieldValidation,
  ContentTypeProps,
  Control,
  EditorInterfaceProps,
  EditorLayoutItem,
  FieldGroupItem,
  GroupControl,
} from 'contentful-management/types';
import { z } from 'zod';

import { sidebarConfig } from './sidebar-config.ts';

// Note: contentful-management/types forgot to export "Item" so we've duplicated it here to avoid ts errors.
export interface Item {
  type: string;
  linkType?: string;
  validations?: ContentTypeFieldValidation[];
}
export interface ContentField extends Omit<ContentFields, 'id' | 'items'> {
  items?: Item;
}

export interface FieldGroupProps {
  /** The id of the Field Group. Please user lower camel case.
   * ie. pageSettings
   */
  groupId: string;
  /** Friendly name of the Field Group. */
  name: string;
  settings?: {
    /** Determines if the Field Group is collapsed by default in the editor. */
    collapsedByDefault?: boolean;
    /** Description for the Field Group. */
    helpText?: string;
  };
}

export interface FieldProps {
  /** The id of the Field. Please user lower camel case.
   * ie. seoTitle
   */
  id: string;
  /** The field properties. */
  contentField: ContentField;
  /** The appearance properties of the field. */
  widget: Omit<Control, 'fieldId'>;
  /** The group this field belongs to. */
  group?: FieldGroupProps;
}

export interface Props extends Omit<ContentTypeProps, 'sys' | 'fields'> {
  /** The id of the Content Type. Please user lower camel case.
   * ie. myContentType
   */
  id: string;
  /** All the fields contained in this Content Type. */
  fields: FieldProps[];
  /** Editor interface configuration for this Content Type. */
  editorInterface?: Omit<EditorInterfaceProps, 'sys' | 'controls'>;
}

export const generateContentType = ({
  id,
  name,
  description,
  displayField,
  fields,
  metadata,
  editorInterface,
}: Props) => {
  const contentGroupId = 'content';
  const contentGroupFields: EditorLayoutItem[] = [];
  const fieldsPerGroup = new Array<FieldGroupItem & GroupControl>();

  fields.forEach(({ group, id: fieldId }) => {
    if (!group) {
      return contentGroupFields.push({ fieldId });
    }
    let existingGroup = fieldsPerGroup.find((g) => g.groupId === group.groupId);
    if (!existingGroup) {
      existingGroup = { ...group, items: [] };
      fieldsPerGroup.push(existingGroup);
    }
    const existingFieldInGroup = existingGroup.items.find(
      (item) => 'fieldId' in item && item.fieldId === fieldId,
    );
    if (!existingFieldInGroup) {
      existingGroup.items.push({ fieldId });
    }
  });

  const output = {
    contentType: {
      sys: {
        id,
        type: 'ContentType',
        publishedVersion: 1,
      },
      name,
      description,
      displayField,
      fields: fields.map((f) => ({
        id: f.id,
        ...f.contentField,
      })),
      metadata,
    },
    editorInterface: {
      sys: {
        id: 'default',
        type: 'EditorInterface',
        contentType: {
          sys: {
            id,
          },
        },
      },
      ...sidebarConfig,
      controls: fields.map((f) => ({
        fieldId: f.id,
        ...f.widget,
      })),
      ...editorInterface,
    },
  };
  const hasMoreThanOneGroup = fieldsPerGroup.length > 0;
  if (hasMoreThanOneGroup) {
    return {
      ...output,
      editorInterface: {
        editorLayout: [
          {
            name: 'Content',
            groupId: 'content',
            items: [
              ...contentGroupFields,
              ...fieldsPerGroup.map(
                ({ groupId, name: groupName, items }) =>
                  ({
                    groupId,
                    name: groupName,
                    items,
                  }) as FieldGroupItem,
              ),
            ],
          },
        ],
        groupControls: [
          {
            groupId: contentGroupId,
            settings: {},
            widgetId: 'topLevelTab',
            widgetNamespace: 'builtin',
          },
          ...fieldsPerGroup.map(
            ({ groupId, settings }) =>
              ({
                groupId,
                settings,
                widgetId: 'fieldset',
                widgetNamespace: 'builtin',
              }) as GroupControl,
          ),
        ],
        ...output.editorInterface,
      },
    };
  }
  return output;
};

export const duplicateContentType = (
  source: ReturnType<typeof generateContentType>,
  newId: string,
  newName: string,
  newDescription: string,
) => {
  return {
    ...source,
    contentType: {
      ...source.contentType,
      sys: { ...source.contentType.sys, id: newId },
      name: newName,
      description: newDescription,
    },
    editorInterface: {
      ...source.editorInterface,
      sys: {
        ...source.editorInterface.sys,
        contentType: {
          ...source.editorInterface.sys.contentType,
          id: newId,
          sys: {
            ...source.editorInterface.sys.contentType.sys,
            id: newId,
          },
        },
      },
    },
  };
};

// NOTE: Don't change these! They drive url structure.
export const LOCALES_CODES = z.enum(['en', 'es', 'ko']);
export type LOCALES_CODES = z.infer<typeof LOCALES_CODES>;

export const FIELD_TYPES = z.enum([
  'Array',
  'Boolean',
  'Date',
  'Integer',
  'Link',
  'Location',
  'Number',
  'Object',
  'ResourceLink',
  'RichText',
  'Symbol',
  'Text',
]);
export type FIELD_TYPES = z.infer<typeof FIELD_TYPES>;

export const LINK_TYPES = z.enum(['Entry', 'Asset']);
export type LINK_TYPES = z.infer<typeof LINK_TYPES>;

export const LINK_MIME_TYPES = z.enum([
  'archive',
  'attachment',
  'audio',
  'code',
  'image',
  'markup',
  'pdfdocument',
  'plaintext',
  'presentation',
  'richtext',
  'spreadsheet',
  'video',
]);
export type LINK_MIME_TYPES = z.infer<typeof LINK_MIME_TYPES>;

export const BUILTIN_WIDGETS = z.enum([
  'assetCard',
  'assetLinkEditor',
  'boolean',
  'checkbox',
  'datePicker',
  'dropdown',
  'entryCardsEditor',
  'entryLinkEditor',
  'entryLinksEditor',
  'markdown',
  'multipleLine',
  'numberEditor',
  'radio',
  'richTextEditor',
  'singleLine',
  'slugEditor',
  'tagEditor',
  'urlEditor',
]);
export type BUILTIN_WIDGETS = z.infer<typeof BUILTIN_WIDGETS>;
